import {useMemo, useRef, useState} from "react";
import {Alert, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {bffApi} from "../../chat/bffApi";
import {
    buildDocumentsExportUrl,
    downloadExport,
    sendExportMail,
} from "../checklist/exportHelpers";
import {ResourceType} from "../types";
import {ChecklistModalActions} from "../checklist/ChecklistModalActions";
import {
    CommentsToggle,
    DeliveryControls,
    DocumentsList,
    TemplateSelect,
} from "./parts";
import {setStoredTemplate, setStoredTopicTemplate} from "./templateStorage";
import type {
    CollectionMeta,
    DeliveryFormat,
    ExportDocument,
    ModalBusyProps,
    TopicMeta,
} from "./types";
import classes from "../checklist/modalStyles.module.css";
import {newMetricEvent} from "../../chat/metrics";

type Props = {
    onClose: () => void;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    documents: ExportDocument[];
    /** Standalone docgen dialog: one template, one Word file, as in the
     *  legacy app. Hides the template list and the delivery choice, and
     *  names the button "Generer dokument". */
    isStandalone?: boolean;
} & ModalBusyProps;

export const GenerateTemplatesTab = ({
    onClose,
    topicMeta,
    collectionMeta,
    documents,
    isStandalone = false,
    busy,
    onBusyChange,
}: Props) => {
    const {t} = useTranslation();
    const {data: home} = dibAppApi.endpoints.getHome.useQuery();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const isdemo = !!user?.demokonto;
    const defaultEmail = user?.epost ?? "";
    const templates = home?.templates ?? [];

    const [finalDocuments, setFinalDocuments] = useState(documents);
    const count = useMemo(
        () => finalDocuments.filter((d) => d.selected).length,
        [finalDocuments]
    );

    const [selectedTemplate, setSelectedTemplate] = useState<string>();
    const [isValid, setIsValid] = useState(true);
    const [validTemplate, setValidTemplate] = useState(true);
    const [trackInteraction] = bffApi.endpoints.trackInteraction.useMutation();
    const [format, setFormat] = useState<DeliveryFormat>("doc");
    const [recipients, setRecipients] = useState<string[]>([defaultEmail]);
    const [withComments, setWithComments] = useState(false);
    const templateRef = useRef<HTMLDivElement>(null);

    const version = new URLSearchParams(window.location.search).get("version");

    const hasSelectableNonPdf =
        finalDocuments.filter(
            (d) => d.selected && d.resourceTypeId !== ResourceType.pdf
        ).length > 0;
    const enabled = isValid && hasSelectableNonPdf;

    const handleTemplateSelect = (id: string) => {
        setSelectedTemplate(id);
        setValidTemplate(true);
    };

    const runGenerate = async () => {
        if (isdemo || !isValid) return;
        const operationId = crypto.randomUUID();
        const selectedDocs = finalDocuments.filter((d) => d.selected);
        const formatParam = format === "doc" || format === "email" ? "docx" : "zip";
        const delivery = format === "email" ? "email" : "download";
        const url = buildDocumentsExportUrl({
            format: formatParam,
            topicId: topicMeta.resourceId,
            collectionId: collectionMeta?.id,
            topics: selectedDocs.map((d) => d.id),
            withComments,
            version: version ?? undefined,
            template: selectedTemplate ?? "0",
            email: format === "email" ? recipients[0] : undefined,
        });

        setStoredTemplate(selectedTemplate);
        setStoredTopicTemplate(topicMeta.topicId, selectedTemplate);

        onBusyChange(true);
        void trackInteraction({
            ...newMetricEvent(),
            action: "export_requested",
            operationId,
            format: formatParam,
            contentType: "templates",
            delivery,
        });
        try {
            if (format === "email") {
                await sendExportMail(url);
            } else {
                await downloadExport(url);
            }
            void trackInteraction({
                ...newMetricEvent(),
                action: "export_completed",
                operationId,
                format: formatParam,
                contentType: "templates",
                delivery,
            });
            if (delivery === "download") {
                // The helper receives the export and calls the browser download.
                void trackInteraction({
                    ...newMetricEvent(),
                    action: "file_download_started",
                    operationId,
                    subjectId: operationId,
                    source: "content",
                    format: formatParam,
                });
            }
            onClose();
        } catch {
            void trackInteraction({
                ...newMetricEvent(),
                action: "export_failed",
                operationId,
                format: formatParam,
                contentType: "templates",
                delivery,
                reason: "unknown",
            });
            onBusyChange(false);
        }
    };

    const handlePrimaryClick = () => {
        if (selectedTemplate === undefined && templates.length !== 0) {
            setValidTemplate(false);
            templateRef.current?.scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: "nearest",
            });
            return;
        }
        void runGenerate();
    };

    return (
        <>
            <div className={classes.modalBody}>
                {isdemo && (
                    <Alert variant="light" color="gray">
                        {t("Info.GenerationNotAvailableForDemoUsers")}
                    </Alert>
                )}
                {isStandalone ? (
                    // `format` keeps its "doc" default: the export is one docx.
                    <Text fw={600} className={classes.modalText}>
                        {topicMeta.name}
                    </Text>
                ) : (
                    <>
                        <DocumentsList
                            areFilesAllowed={false}
                            count={count}
                            documents={finalDocuments}
                            setDocuments={setFinalDocuments}
                            setIsValid={setIsValid}
                        />
                        <DeliveryControls
                            format={format}
                            onSetFormat={setFormat}
                            recipients={recipients}
                            onSetRecipients={setRecipients}
                        />
                    </>
                )}
                <div ref={templateRef}>
                    <TemplateSelect
                        topicId={topicMeta.topicId}
                        selectedTemplate={selectedTemplate}
                        onSelect={handleTemplateSelect}
                        error={!validTemplate}
                    />
                </div>
                <CommentsToggle
                    withComments={withComments}
                    setWithComments={setWithComments}
                />
            </div>
            <ChecklistModalActions
                primary={{
                    // Per dialog, as in the legacy app: the standalone modal
                    // always says "Generer dokument", the checklist modal always
                    // "Generer maler", whatever the number of ticked templates.
                    label: isStandalone
                        ? t("Action.GenerateDocument")
                        : t("Action.GenerateTemplates"),
                    onClick: handlePrimaryClick,
                    loading: busy,
                    disabled: isdemo || !enabled,
                }}
                secondary={{
                    label: t("Word.Abort"),
                    onClick: onClose,
                    disabled: busy,
                }}
            />
        </>
    );
};
